import React, { Suspense } from 'react';
import _ from 'lodash';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { RoleService, AuthService } from 'Services';
import ForbiddenPage from 'Routes/ForbiddenPage';
import { getFlatRoutesArray } from 'Components/Breadcrumbs';
import NotFound from 'Components/ErrorScreens/NotFound';
import PageElement from '@brighterion/templates/PageElement';
import RootElement from '@brighterion/templates/RootElement';
import { AuthConfig } from '@brighterion/types/config';
import { ROUTES } from 'Components/Breadcrumbs/routes';
import CustomErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import ImportExportSidePanel from 'Components/SidePanel/ImportExportSidePanel';
import RouteLoading from 'Components/RouteLoading';
import AccessPending from 'Components/AccessPending';

const TemplateConfigAndUsers: React.FC = ({ children }) => {
    /**
     * Please use React-Query instead:
     * @example
     * const queryClient = useQueryClient();
     * const templateConfig = queryClient.getQueryData<TemplateConfig>([RQ_TEMPLATE_CONFIG]);
     */

    return <>{children}</>;
};

type AppProps<T = any> = {
    authConfig: T;
};

const Routes: React.FC<AppProps<AuthConfig>> = ({ authConfig }) => {
    const { hasWLSConfigAdminAccess, isLandlord } = RoleService;

    // LOGOUT for MC Connect
    React.useEffect(() => {
        // https://auth-broker.brighterion.io/auth-broker/oauth
        window.addEventListener('connectsignout', (event: Event) => {
            event.preventDefault();
            AuthService.logout().then(() => {
                event.detail.resume();
            });
        });

        window.addEventListener('connectsignoutcomplete', () => {
            AuthService.logout();
        });

        return () => {
            window.removeEventListener('connectsignout', () => {});
            window.removeEventListener('connectsignoutcomplete', () => {});
        };
    }, []);

    const isMastercardConnectUser = typeof authConfig?.idp === 'undefined' || authConfig?.idp === 'mcconnect';

    // Initialize hasAccess
    const hasAccess = true;

    return (
        <TemplateConfigAndUsers>
            <Router>
                {!hasAccess ? (
                    <AccessPending />
                ) : (
                    <RootElement options={{ isMastercardConnectUser }}>
                        <CustomErrorBoundary bodyMessage='Something went wrong.'>
                            <ImportExportSidePanel />
                            <Switch>
                                <Route
                                    exact
                                    path='/'
                                    render={() => {
                                        if (isLandlord) {
                                            return <Redirect to={ROUTES.TENANTS.INDEX} />;
                                        }
                                        if (hasWLSConfigAdminAccess) {
                                            return <Redirect to={ROUTES.CONFIGURATION.INDEX} />;
                                        }

                                        return <Redirect to='/403' />;
                                    }}
                                />
                                {getFlatRoutesArray().map(({ component: Component, path }) => (
                                    <Route
                                        key={_.uniqueId('route_')}
                                        exact
                                        path={path}
                                        render={(props) => {
                                            // Redirect landlord users to tenants page if they try to access other routes
                                            if (isLandlord && path !== ROUTES.TENANTS.INDEX) {
                                                return <Redirect to={ROUTES.TENANTS.INDEX} />;
                                            }

                                            return Component ? (
                                                <PageElement head={{ title: props.location.pathname }}>
                                                    <Suspense fallback={<RouteLoading />}>
                                                        <Component {...props} />
                                                    </Suspense>
                                                </PageElement>
                                            ) : null;
                                        }}
                                    />
                                ))}

                                <Route key='forbiddenPage' path='/403'>
                                    <PageElement head={{ title: '403 Forbidden' }}>
                                        <ForbiddenPage />
                                    </PageElement>
                                </Route>

                                <Route key='notFoundPage' path='*'>
                                    <PageElement head={{ title: '404 Page Not Found' }}>
                                        <NotFound />
                                    </PageElement>
                                </Route>
                            </Switch>
                        </CustomErrorBoundary>
                    </RootElement>
                )}
            </Router>
        </TemplateConfigAndUsers>
    );
};

export default Routes;
